<template>
  <div class="step-content py-4 p-0 pt-1">
    <h3 class="step-title">Confirme seu evento</h3>
    <p class="step-description">Revise as informações antes de criar sua lista.</p>

    <!-- Card de resumo -->
    <div class="confirm-card mb-4">

      <!-- Foto + nome + tipo -->
      <div class="confirm-card__header">
        <div class="confirm-card__photo">
          <img
            v-if="eventData.eventPhotoUrl"
            :src="eventData.eventPhotoUrl"
            :alt="eventData.eventName"
          />
          <div v-else class="confirm-card__photo-placeholder">
            <i class="fas fa-gift"></i>
          </div>
        </div>
        <div class="confirm-card__identity">
          <p class="confirm-card__event-name">
            {{ eventData.eventName || 'Sem nome definido' }}
          </p>
          <span class="confirm-card__event-type">
            {{ eventData.eventTypeDisplay || '—' }}
          </span>
          <p v-if="eventData.eventDate" class="confirm-card__event-date">
            <i class="far fa-calendar-alt me-1"></i>
            {{ formattedDate }}
          </p>
        </div>
      </div>

      <!-- Divisor -->
      <div class="confirm-card__divider"></div>

      <!-- Resumo dos itens -->
      <div class="confirm-card__items-section">
        <p class="confirm-card__items-title">
          <i class="fas fa-gift me-1"></i>
          {{ orderedItems.length }} presente{{ orderedItems.length !== 1 ? 's' : '' }} selecionado{{ orderedItems.length !== 1 ? 's' : '' }}
        </p>
        <div class="confirm-items-scroll">
          <div
            v-for="item in orderedItems"
            :key="item.id"
            class="confirm-item-chip"
          >
            <img
              v-if="item.imageUrl"
              :src="item.imageUrl"
              :alt="item.name"
              class="confirm-item-chip__img"
            />
            <div class="confirm-item-chip__info">
              <span class="confirm-item-chip__name">{{ item.name }}</span>
              <span class="confirm-item-chip__price">
                {{ formatCurrency(item.price) }}
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import { defineExpose, computed } from 'vue';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { formatDateLong } from '@/utils/dateFormatter';

export default {
  name: 'Step3Confirmation',
  props: {
    eventData: {
      type: Object,
      default: () => ({
        eventType: null,
        eventTypeDisplay: '',
        selectedItems: {},
        eventName: '',
        eventDate: '',
        eventPhotoUrl: null, // MUDANÇA AQUI: De eventPhoto para eventPhotoUrl
        eventPhotoFile: null // Adicionado para manter consistência, embora não usado na exibição aqui
      })
    }
  },
  setup(props) {
    const orderedItems = computed(() => {
        return Object.values(props.eventData.selectedItems).sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
        });
    });

    const totalItemsValue = computed(() => {
        return orderedItems.value.reduce((sum, item) => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseInt(item.quantity) || 1;
            return sum + (price * quantity);
        }, 0);
    });

    const validateAndProceed = (isFinalValidation = false) => {
      const isValid = props.eventData.eventType && Object.keys(props.eventData.selectedItems).length > 0;
      return {
        isValid: true, 
        data: props.eventData
      };
    };

    const formattedDate = computed(() => {
      if (!props.eventData.eventDate) return null;
      return formatDateLong(props.eventData.eventDate);
    });

    defineExpose({
      validateAndProceed,
    });

    return {
      validateAndProceed,
      formatCurrency,
      orderedItems,
      totalItemsValue,
      formattedDate
    };
  }
};
</script>

<style scoped>
.step-title  { font-size: 1.25rem; font-weight: 700; color: #32355d; margin-bottom: 0.25rem; }
.step-description { font-size: 0.9rem; color: #6c757d; margin-bottom: 1.5rem; }

/* ── Card de confirmação ──────────────────────────────────────── */
.confirm-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

.confirm-card__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
}

.confirm-card__photo {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2.5px solid #ff50a3;
  box-shadow: 0 2px 8px rgba(255, 80, 163, 0.2);
}

.confirm-card__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.confirm-card__photo-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #ff50a3, #ff8cc8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
}

.confirm-card__identity {
  flex: 1;
  min-width: 0;
}

.confirm-card__event-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #32355d;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.confirm-card__event-type {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  background: #fff0f6;
  color: #ff50a3;
  border-radius: 99px;
  padding: 2px 10px;
  margin-bottom: 4px;
}

.confirm-card__event-date {
  font-size: 0.78rem;
  color: #9e9e9e;
  margin: 0;
}

.confirm-card__divider {
  height: 1px;
  background: #f5f5f5;
  margin: 0 1.25rem;
}

/* Seção de itens */
.confirm-card__items-section {
  padding: 1rem 1.25rem 1.25rem;
}

.confirm-card__items-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.confirm-items-scroll {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
}

.confirm-item-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 0.75rem;
  padding: 8px 10px;
}

.confirm-item-chip__img {
  width: 36px;
  height: 36px;
  border-radius: 0.5rem;
  object-fit: cover;
  flex-shrink: 0;
}

.confirm-item-chip__info {
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 8px;
}

.confirm-item-chip__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.3;
}

.confirm-item-chip__price {
  font-size: 0.82rem;
  font-weight: 700;
  color: #ff50a3;
  flex-shrink: 0;
}
</style>
